import { useMemo, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';

import { getNotificationListQueryKeyPrefix } from '@frontend/api';

import { useSubscribeWebsocket } from 'utils/hooks';

import type { FiltersType } from '../types';
import { QUERY_PARAMS } from './constants';

const LOCATION = 'notifications';

export const useQueryParam = (queryParam: string): string | null => {
  /*
  Returns the requested query param from the url. URL parameters are casted as strings.
  */
  const { search } = useLocation();

  return useMemo(
    () => new URLSearchParams(search).get(queryParam),
    [search, queryParam],
  );
};

type SetQueryParamProps = {
  queryParam: string;
  value?: string;
};

export const useSetQueryParam = () => {
  /*
  Overrides the desired query param without replacing other ones.
  */
  const { search } = useLocation();
  const history = useHistory();

  const setQueryParam = useCallback(
    (queryParams: SetQueryParamProps[]) => {
      const urlParams = new URLSearchParams(search);
      queryParams.forEach(({ queryParam, value }) => {
        if (value === undefined) {
          urlParams.delete(queryParam);
        } else {
          urlParams.set(queryParam, value);
        }
      });

      history.replace(`/${LOCATION}?${urlParams.toString()}`);
    },
    [search, history],
  );

  return setQueryParam;
};

export const useNotificationFilters = (): [
  FiltersType,
  (arg0: FiltersType) => void,
] => {
  const setQueryParam = useSetQueryParam();

  const setNotificationFilters = useCallback(
    ({
      searchTerm,
      organizationIds,
      dateMin,
      dateMax,
      page,
      filterOutAcknowledged,
      dateRange,
    }: FiltersType): void => {
      setQueryParam([
        { queryParam: QUERY_PARAMS.SEARCH_TERM, value: searchTerm },
        {
          queryParam: QUERY_PARAMS.ORGANIZATION_IDS,
          value: organizationIds?.join(','),
        },
        { queryParam: QUERY_PARAMS.DATE_MIN, value: dateMin },
        { queryParam: QUERY_PARAMS.DATE_MAX, value: dateMax },
        { queryParam: QUERY_PARAMS.PAGE, value: page?.toString() },
        {
          queryParam: QUERY_PARAMS.FILTER_OUT_ACKNOWLEDGED,
          value:
            filterOutAcknowledged === undefined
              ? undefined
              : filterOutAcknowledged.toString(),
        },
        { queryParam: QUERY_PARAMS.DATE_RANGE, value: dateRange },
      ]);
    },
    [setQueryParam],
  );

  const nullToUndefined = (x: string | null): string | undefined =>
    x === null ? undefined : x;

  const searchTerm = nullToUndefined(useQueryParam(QUERY_PARAMS.SEARCH_TERM));
  const organizationIds = useQueryParam(QUERY_PARAMS.ORGANIZATION_IDS)
    ?.split(',')
    .map((str) => parseInt(str));
  const dateMin = nullToUndefined(useQueryParam(QUERY_PARAMS.DATE_MIN));
  const dateMax = nullToUndefined(useQueryParam(QUERY_PARAMS.DATE_MAX));
  const pageString = useQueryParam(QUERY_PARAMS.PAGE);
  const page = pageString === null ? undefined : parseInt(pageString);
  const filterOutAcknowledgedString = useQueryParam(
    QUERY_PARAMS.FILTER_OUT_ACKNOWLEDGED,
  );
  const filterOutAcknowledged =
    filterOutAcknowledgedString === null
      ? undefined
      : filterOutAcknowledgedString === 'true';

  const dateRange = nullToUndefined(useQueryParam(QUERY_PARAMS.DATE_RANGE));

  const filters = useMemo(
    () => ({
      searchTerm,
      organizationIds,
      dateMin,
      dateMax,
      page,
      filterOutAcknowledged,
      dateRange,
    }),
    [
      searchTerm,
      organizationIds,
      dateMin,
      dateMax,
      page,
      filterOutAcknowledged,
      dateRange,
    ],
  );

  return [filters, setNotificationFilters];
};

export const useWebsocketFetchNewNotifications = () => {
  const queryClient = useQueryClient();
  const cb = useCallback(async () => {
    queryClient.invalidateQueries({
      queryKey: getNotificationListQueryKeyPrefix(),
    });
  }, [queryClient]);
  useSubscribeWebsocket('notifications', cb);
};
